import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';

interface AuditLogEntry {
  timestamp: string;
  admin_user_id: string;
  admin_email: string;
  admin_name: string;
  action: string;
  target_user_id?: string;
  target_email?: string;
  target_name?: string;
  details: Record<string, any>;
}

interface AuditLogResponse {
  entries: AuditLogEntry[];
  total_count: number;
  has_more: boolean;
}

interface AdminAuditLogProps {
  maxEntries?: number;
  showTitle?: boolean;
  className?: string;
}

const AdminAuditLog: React.FC<AdminAuditLogProps> = ({
  maxEntries = 50,
  showTitle = true,
  className = ''
}) => {
  const { userRole } = useAuth();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterAction, setFilterAction] = useState<string>('all');

  const loadAuditLog = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get(`/v1/admin/audit-log?limit=${maxEntries}`);
      const data: AuditLogResponse = response.data.data;

      setEntries(data.entries);
    } catch (err: any) {
      console.error('Failed to load audit log:', err);
      setError(err.response?.data?.error?.message || 'Failed to load audit log');
    } finally {
      setIsLoading(false);
    }
  }, [maxEntries]);

  useEffect(() => {
    if (userRole === 'admin') {
      loadAuditLog();
    }
  }, [userRole, maxEntries, loadAuditLog]);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'admin_invitation_sent':
        return 'fas fa-paper-plane text-info';
      case 'admin_invitation_accepted':
        return 'fas fa-check-circle text-success';
      case 'admin_invitation_rejected':
        return 'fas fa-times-circle text-danger';
      case 'admin_role_relinquished':
        return 'fas fa-user-minus text-warning';
      case 'user_account_locked':
        return 'fas fa-lock text-danger';
      case 'user_account_unlocked':
        return 'fas fa-unlock text-success';
      case 'system_settings_updated':
        return 'fas fa-cog text-primary';
      default:
        return 'fas fa-info-circle text-muted';
    }
  };

  const getActionDescription = (entry: AuditLogEntry) => {
    const adminName = entry.admin_name || entry.admin_email;
    const targetName = entry.target_name || entry.target_email || 'Unknown User';

    switch (entry.action) {
      case 'admin_invitation_sent':
        return `${adminName} sent admin invitation to ${targetName}`;
      case 'admin_invitation_accepted':
        return `${targetName} accepted admin invitation from ${adminName}`;
      case 'admin_invitation_rejected':
        return `${targetName} rejected admin invitation from ${adminName}`;
      case 'admin_role_relinquished':
        return `${adminName} relinquished admin role`;
      case 'user_account_locked':
        return `${adminName} locked account for ${targetName}`;
      case 'user_account_unlocked':
        return `${adminName} unlocked account for ${targetName}`;
      case 'system_settings_updated':
        return `${adminName} updated system settings`;
      default:
        return `${adminName} performed ${entry.action}`;
    }
  };

  const getActionBadgeClass = (action: string) => {
    switch (action) {
      case 'admin_invitation_sent':
        return 'bg-info';
      case 'admin_invitation_accepted':
        return 'bg-success';
      case 'admin_invitation_rejected':
        return 'bg-danger';
      case 'admin_role_relinquished':
        return 'bg-warning text-dark';
      case 'user_account_locked':
        return 'bg-danger';
      case 'user_account_unlocked':
        return 'bg-success';
      case 'system_settings_updated':
        return 'bg-primary';
      default:
        return 'bg-secondary';
    }
  };

  const formatActionName = (action: string) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const filteredEntries = (entries || []).filter(entry => {
    if (filterAction === 'all') return true;
    return entry.action === filterAction;
  });

  const uniqueActions = Array.from(new Set((entries || []).map(entry => entry.action)));

  if (userRole !== 'admin') {
    return null;
  }

  return (
    <div className={className}>
      {showTitle && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            <i className="fas fa-history me-2"></i>
            Admin Audit Log
          </h5>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm"
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="all">All Actions</option>
              {uniqueActions.map(action => (
                <option key={action} value={action}>
                  {formatActionName(action)}
                </option>
              ))}
            </select>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={loadAuditLog}
              disabled={isLoading}
            >
              <i className="fas fa-sync-alt me-1"></i>
              Refresh
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Error:</strong> {error}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading audit log...</p>
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className="text-center py-4">
          <i className="fas fa-history fa-3x text-muted mb-3"></i>
          <h6 className="text-muted">No audit entries found</h6>
          <p className="text-muted small">
            {filterAction === 'all'
              ? 'No admin actions have been logged yet.'
              : `No ${formatActionName(filterAction).toLowerCase()} actions found.`
            }
          </p>
        </div>
      ) : (
        <div className="list-group">
          {filteredEntries.map((entry, index) => (
            <div key={`${entry.timestamp}-${index}`} className="list-group-item">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center mb-2">
                    <i className={`${getActionIcon(entry.action)} me-2`}></i>
                    <span className="fw-medium">
                      {getActionDescription(entry)}
                    </span>
                    <span className={`badge ${getActionBadgeClass(entry.action)} ms-2`}>
                      {formatActionName(entry.action)}
                    </span>
                  </div>

                  {Object.keys(entry.details).length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">Details:</small>
                      <div className="ms-3">
                        {Object.entries(entry.details).map(([key, value]) => (
                          <div key={key} className="small text-muted">
                            <strong>{key.replace(/_/g, ' ')}:</strong> {
                              typeof value === 'object' ? JSON.stringify(value) : String(value)
                            }
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-end">
                  <small className="text-muted">
                    {new Date(entry.timestamp).toLocaleString()}
                  </small>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredEntries.length > 0 && entries.length >= maxEntries && (
        <div className="text-center mt-3">
          <small className="text-muted">
            Showing last {filteredEntries.length} entries.
            <button
              className="btn btn-link btn-sm p-0 ms-1"
              onClick={() => window.location.href = '/admin/audit-log'}
            >
              View full audit log
            </button>
          </small>
        </div>
      )}
    </div>
  );
};

export default AdminAuditLog;
